<template>
  <div class="bs" data-test="workflow-settings-root">
    <div class="bs__scrim" aria-hidden="true" data-test="workflow-settings-scrim" @click="wfBuilder.closeSettings()"></div>

    <aside
      ref="sheet"
      class="bs__sheet"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
      tabindex="-1"
      data-test="workflow-settings-panel"
    >
      <header class="bs__head">
        <span class="bs__tile"><WfIcon :name="iconName" :size="20" /></span>
        <div class="bs__titles">
          <div class="bs__title" data-test="workflow-settings-title">{{ title }}</div>
          <div class="bs__sub">
            <span v-if="subtitle" data-test="workflow-settings-sub">{{ subtitle }}</span>
            <span v-if="context" class="bs__ctx" data-test="workflow-settings-context">
              <WfIcon :name="context.icon" :size="13" />
              {{ context.text }}
            </span>
          </div>
        </div>
        <button
          type="button"
          class="bs__close"
          aria-label="Close"
          data-test="workflow-settings-close"
          @click="wfBuilder.closeSettings()"
        >
          <WfIcon name="x" :size="18" />
        </button>
      </header>

      <!-- The ONE scroll container. Nothing inside it clips, and 240px of room under the last field lets
           the last picker's list open downward. -->
      <div class="bs__body" data-test="workflow-settings-body">
        <div class="bs__inner">
          <TaskSettings v-if="type === 'assigntask'" />
          <MessageSettings v-else-if="type === 'sms'" channel="sms" />
          <MessageSettings v-else-if="type === 'email'" channel="email" />
          <KanbanSettings v-else-if="type === 'kanban'" />
          <FormSettings v-else-if="type === 'sendform'" />
          <WaitSettings v-else-if="type === 'wait'" />
          <SlackSettings v-else-if="type === 'slack'" />
          <TriggerSettings v-else-if="type === 'mangovoice' || type === 'xfollowups'" :kind="type" />
          <p v-else class="bs__none" data-test="workflow-settings-none">This block has no settings.</p>
        </div>
      </div>

      <footer class="bs__foot" data-test="workflow-settings-footer">
        <template v-if="ask">
          <span class="bs__ask" role="alert" data-test="workflow-settings-ask">{{ askText }}</span>
          <button type="button" class="bs__btn bs__btn--ghost" data-test="workflow-settings-ask-no" @click="ask = ''">
            {{ askNo }}
          </button>
          <button
            type="button"
            class="bs__btn"
            :class="ask === 'delete' ? 'bs__btn--danger' : 'bs__btn--primary'"
            data-test="workflow-settings-ask-yes"
            @click="onAskYes"
          >{{ askYes }}</button>
        </template>

        <template v-else>
          <button type="button" class="bs__btn bs__btn--ghost bs__btn--delete" data-test="workflow-settings-delete" @click="ask = 'delete'">
            <WfIcon name="trash" :size="16" />
            Delete block
          </button>
          <span class="bs__spacer"></span>
          <span v-if="dirty" class="bs__dirty" data-test="workflow-settings-dirty">Unsaved changes</span>
          <button type="button" class="bs__btn bs__btn--secondary" data-test="workflow-settings-cancel" @click="wfBuilder.closeSettings()">
            Cancel
          </button>
          <button type="button" class="bs__btn bs__btn--primary" data-test="workflow-settings-save" @click="onSave">
            Save changes
          </button>
        </template>
      </footer>
    </aside>
  </div>
</template>

<script>
/**
 * The redesigned BLOCK SETTINGS sheet (design frames 1g-1p; Workflow overhaul P4, 2026-10-10): a 760px sheet
 * over a scrim, with the block's icon, name, a one-line description and a chip saying which trigger the
 * workflow starts from; ONE scroll body holding the panel for the block; and a footer with Delete block,
 * "Unsaved changes", Cancel and Save changes.
 *
 * ⛔ THE CONTRACT WITH THE BUILDER PAGE. The page `provide()`s `wfBuilder` = itself, a component that
 * `extends` the classic WorkflowBuilder, so every classic field, computed and method is `wfBuilder.<name>`.
 * The sheet takes NO props; the page mounts it as `<BlockSettingsSheet v-if="showSettings" />`.
 *   - The node being edited is `wfBuilder.selectedNodeData` (a deep clone made by the classic
 *     `openNodeSettings`); the panels bind straight into its `properties`, as the classic template does.
 *   - Cancel, X, the scrim and Escape call `wfBuilder.closeSettings()`.
 *   - Save changes calls `wfBuilder.commitSettings()`, except that a Kanban action with no proc codes
 *     (`wfBuilder.needsEmptyProcCodesConfirm(node)`) first turns the footer into an inline ask: it would
 *     run for EVERY procedure the trigger fires on. "Go back" stays, "Save anyway" commits. (The classic
 *     builder asks with a browser dialog; the redesign never does.)
 *   - Delete block asks inline ("Delete this block? Its settings are lost."), then calls
 *     `wfBuilder.removeSelectedNode()`.
 *   - "Unsaved changes" compares the node with a JSON snapshot taken when the sheet mounts.
 *
 * Escape closes the sheet, except that it first cancels an ask that is showing, and it leaves an open
 * picker list to close itself (the widget handles Escape on its own).
 *
 * ⛔ No prop named patNum / siteID / loading / allSettings / isSuperAdmin / userID.
 */
import WfIcon from './WfIcon.vue';
import TaskSettings from './settings/TaskSettings.vue';
import MessageSettings from './settings/MessageSettings.vue';
import KanbanSettings from './settings/KanbanSettings.vue';
import FormSettings from './settings/FormSettings.vue';
import WaitSettings from './settings/WaitSettings.vue';
import SlackSettings from './settings/SlackSettings.vue';
import TriggerSettings from './settings/TriggerSettings.vue';
import { actionByType, triggerByType, resolveTrigger, DEFAULT_ACTION_ICON } from '@/utils/workflow/blockCatalog';

// The design's one-line description under the block's name (META in the design export).
const SUBTITLES = {
  assigntask: 'Creates a task for someone on your team',
  sms: 'Texts the patient',
  email: 'Emails the patient',
  kanban: 'Adds, updates, or moves a card on a Kanban board',
  sendform: 'Sends the patient a form to sign',
  wait: 'Waits before the next step',
  slack: 'Posts a message in a Slack channel',
  mangovoice: 'Starts this workflow on a phone event',
  xfollowups: 'Starts this workflow after follow-ups are completed',
};

function snapshotOf(node) {
  try {
    return JSON.stringify(node);
  } catch (e) {
    return '';
  }
}

export default {
  name: 'BlockSettingsSheet',
  components: { WfIcon, TaskSettings, MessageSettings, KanbanSettings, FormSettings, WaitSettings, SlackSettings, TriggerSettings },
  inject: ['wfBuilder'],

  data() {
    return {
      // '' | 'delete' | 'every' (Kanban, no proc codes)
      ask: '',
      snapshot: '',
    };
  },

  computed: {
    node() {
      return this.wfBuilder.selectedNodeData || {};
    },
    type() {
      return this.node.type || '';
    },
    entry() {
      return actionByType(this.type) || triggerByType(this.type);
    },
    isTrigger() {
      return !!triggerByType(this.type);
    },
    title() {
      return this.entry ? this.entry.label : 'Settings';
    },
    subtitle() {
      return SUBTITLES[this.type] || (this.entry ? this.entry.description : '');
    },
    iconName() {
      return this.entry ? this.entry.icon : DEFAULT_ACTION_ICON;
    },
    // "Trigger" for a trigger's own settings; "After <the workflow's trigger>" for an action.
    context() {
      if (this.isTrigger) return { icon: this.iconName, text: 'Trigger' };
      const trigger = resolveTrigger(this.wfBuilder.currentTrigger);
      if (!trigger) return null;
      return { icon: trigger.icon, text: `After ${trigger.label.charAt(0).toLowerCase()}${trigger.label.slice(1)}` };
    },
    dirty() {
      return this.snapshot !== '' && snapshotOf(this.node) !== this.snapshot;
    },
    askText() {
      return this.ask === 'every'
        ? 'Save with no proc codes? It will run for every procedure this trigger fires on.'
        : 'Delete this block? Its settings are lost.';
    },
    askNo() {
      return this.ask === 'every' ? 'Go back' : 'Cancel';
    },
    askYes() {
      return this.ask === 'every' ? 'Save anyway' : 'Delete block';
    },
  },

  mounted() {
    this.snapshot = snapshotOf(this.node);
    document.addEventListener('keydown', this.onKeydown);
    this.$nextTick(() => {
      const sheet = this.$refs.sheet;
      if (sheet && typeof sheet.focus === 'function') sheet.focus();
    });
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  },

  methods: {
    onSave() {
      // Empty proc codes on a Kanban action run it for EVERY procedure: ask first, in the footer.
      if (this.wfBuilder.needsEmptyProcCodesConfirm(this.node)) {
        this.ask = 'every';
        return;
      }
      this.wfBuilder.commitSettings();
    },
    onAskYes() {
      const ask = this.ask;
      this.ask = '';
      if (ask === 'delete') this.wfBuilder.removeSelectedNode();
      else this.wfBuilder.commitSettings();
    },
    onKeydown(event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      // An open picker closes itself on Escape; the next press closes the sheet.
      const target = event.target;
      if (target && typeof target.closest === 'function' && target.closest('.multiselect--active')) return;
      if (this.ask) {
        this.ask = '';
        return;
      }
      this.wfBuilder.closeSettings();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './workflow-tokens';

// The scrim and the sheet are fixed over the whole app, above the floating agent button (z 9999).
.bs {
  @include wf-page-root;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: $wf-z-dialog;
}

.bs__scrim {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(12, 15, 14, 0.3);
}

.bs__sheet {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: $wf-z-sheet;
  display: flex;
  flex-direction: column;
  width: $wf-sheet-width;
  max-width: 100%;
  background: $fu-surface;
  border-left: 1px solid $fu-border;
  box-shadow: $fu-shadow-xl;
  color: $fu-text;

  &:focus { outline: none; }

  @include wf-tablet { width: 100%; border-left: 0; }
}

// ---- header ---------------------------------------------------------------------------------------
.bs__head {
  flex: none;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 16px 18px 28px;
  border-bottom: 1px solid $fu-border;

  @include wf-phone { padding-left: 16px; }
}

.bs__tile {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 11px;
  background: $fu-brand-50;
  color: $fu-brand-700;
}

.bs__titles {
  flex: 1 1 auto;
  min-width: 0;
}

.bs__title {
  font-family: $fu-font-display;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.bs__sub {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 5px;
  font-size: 13px;
  color: $fu-neutral-500;
}

.bs__ctx {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 9px;
  border-radius: 999px;
  background: $fu-neutral-100;
  color: $fu-neutral-700;
  font-size: 12.5px;
  font-weight: 600;
}

.bs__close {
  @include fu-icon-btn(36px);

  @include wf-touch { width: 44px; height: 44px; }
}

// ---- body: the only scroller -----------------------------------------------------------------------
.bs__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: $fu-neutral-25;
}

// 240px of room under the last field so its list can open downward.
.bs__inner {
  position: relative;
  padding: 24px 28px 240px;

  @include wf-phone { padding: 20px 16px 240px; }
}

.bs__none {
  margin: 0;
  font-size: 14px;
  color: $fu-neutral-500;
}

// ---- footer ----------------------------------------------------------------------------------------
.bs__foot {
  flex: none;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 14px 24px 14px 20px;
  border-top: 1px solid $fu-border;
  background: $fu-surface;

  @include wf-phone { padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px)); }
}

.bs__spacer { flex: 1 1 0; }

.bs__dirty {
  margin-right: 4px;
  font-size: 13px;
  font-weight: 600;
  color: $fu-neutral-500;
}

.bs__ask {
  flex: 1 1 240px;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
}

.bs__btn {
  &--primary { @include fu-btn(primary, md); }
  &--secondary { @include fu-btn(secondary, md); }
  &--ghost { @include fu-btn(ghost, md); }
  &--danger { @include fu-btn(danger, md); }

  @include wf-touch { height: 44px; }
}

.bs__btn--delete ::v-deep .wf-icon { color: $fu-danger; }
</style>
